import React, { useEffect, useState } from "react";
import { Box, FormControl, FormLabel, Input, Option, Select, Sheet, Textarea, Typography } from "@mui/joy";
import { useGetHandTDataMutation } from "../services/invoices/InvoiceApiSlice";
import { useGetSelectedH_TListMutation } from "../services/vendor/VendorApi";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Describes a single field in our form grid
export interface FormField {
  name: string;
  label: string;
  type?: "text" | "number" | "date" | "textarea" | "select" | "H"; // <-- Add 'select' type
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  gridSpan?: number;
  readOnly?: boolean;
  // Options for the select dropdown
  options?: { label: string; value: string }[];
}

interface FormGridProps {
  fields: FormField[];
  formData: Record<string, any>;
  onFormChange: (name: string, value: string) => void;
  title?: string;
  setDataApproverList?: any;
}

export const FormGrid: React.FC<FormGridProps> = ({ fields, formData, onFormChange, title, setDataApproverList }) => {
  const params = useParams();

  // const [hntData, setHntData] = useState<any>([{Ebeln: "3500000094", Konnr: "ht"}]);
  const [hntData, setHntData] = useState<any>([]);
  const [getH_TData] = useGetHandTDataMutation(formData.poContract);
  const [getSelectedH_TData] = useGetSelectedH_TListMutation();
  const getData = async () => {
    try {
      const response: any = await getH_TData(formData.poContract.replace("&", "!^!"));
      if (response?.data?.success) {
        const { contract_po_list } = response?.data;
        setHntData([...contract_po_list]);
      }
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (formData.procurement_type === "H") {
      getData();
    }
  }, []);

  const getGrnData = async () => {
    try {
      const response: any = await getSelectedH_TData({ po_contract: formData?.hnt_value, bts_no: params?.id ?? "" });
      setDataApproverList({ ...response?.data });
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (formData?.hnt_value) {
      getGrnData();
    }
  }, [formData]);

  if (!Array.isArray(fields)) {
    return <Typography color="danger">Form fields configuration is missing.</Typography>;
  }

  // Generic handler for text/textarea inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFormChange(e.target.name, e.target.value);
  };

  // Specific handler for Select components
  const handleSelectChange = (event: React.SyntheticEvent | null, newValue: string | null, name: string) => {
    onFormChange(name, newValue || "");
  };

  return (
    <Sheet variant="soft" sx={{ p: 2, borderRadius: "sm" }}>
      {formData.procurement_type === "H" && (
        <Typography level="title-lg" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      {formData.procurement_type !== "H" && (
        <Typography level="title-lg" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            lg: "repeat(4, 1fr)",
            sm: "repeat(2, 1fr)",
            xs: "1fr",
          },
          gap: 2,
        }}
      >
        {fields.map((field) => {
          const gridColumn = field.gridSpan ? `span ${field?.gridSpan}` : "span 1";
          let inputComponent;

          // Switch based on the field type to render the correct component
          switch (field.type) {
            case "select":
              inputComponent = (
                <Select
                  className={`${field?.readOnly ? "only-readonly" : ""}`}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={(event, newValue) => !field.readOnly && handleSelectChange(event, newValue, field.name)}
                  placeholder={field.placeholder}
                  slotProps={{
                    button: {
                      onMouseDown: (e) => field.readOnly && e.preventDefault(), // Prevent dropdown opening
                      sx: field.readOnly ? { pointerEvents: "none", backgroundColor: "transparent" } : {},
                    },
                  }}
                >
                  {(field.options || []).map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              );
              break;
            case "H":
              inputComponent = (
                <Select
                  name={"hnt_value"}
                  // value={hntValue}
                  // onChange={(event, newValue) => setHntValue(newValue || "")}
                  value={formData["hnt_value"] || ""}
                  onChange={(event, newValue) => !field.readOnly && handleSelectChange(event, newValue, "hnt_value")}
                  placeholder={field.placeholder}
                // slotProps={{
                //   button: {
                //     onMouseDown: (e) => field.readOnly && e.preventDefault(), // Prevent dropdown opening
                //     sx: field.readOnly ? { pointerEvents: "none", backgroundColor: "transparent" } : {},
                //   },
                // }}
                >
                  {(hntData || []).map((option) => (
                    <Option key={option.Ebeln} value={option.Ebeln}>
                      {option.Ebeln}
                    </Option>
                  ))}
                </Select>
              );
              break;
            case "textarea":
              inputComponent = (
                <Textarea
                  className={`${field?.readOnly ? "only-readonly" : ""}`}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                  required={field.required}
                  readOnly={field?.readOnly}
                  minRows={2}
                />
              );
              break;
            default:
              inputComponent = (
                <Input
                  className={`${field?.readOnly ? "only-readonly" : ""}`}
                  name={field.name}
                  value={formData[field.name] || null}
                  onChange={handleInputChange}
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  disabled={field.disabled}
                  readOnly={field?.readOnly}
                  required={field.required}
                />
              );
          }

          return (
            <FormControl
              key={field.name}
              required={field.required}
              sx={{
                gridColumn: {
                  sm: "span 1",
                  lg: gridColumn,
                },
              }}
            >
              {field?.type === "H" ? (
                formData?.procurement_type === "H" && (
                  <>
                    <FormLabel>{field.label}</FormLabel>
                    {inputComponent}
                  </>
                )
              ) : (
                <>
                  <FormLabel>{field.label}</FormLabel>
                  {inputComponent}
                </>
              )}
            </FormControl>
          );
        })}
      </Box>
    </Sheet>
  );
};
